import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExchangeAlt } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import {
  UNIT_CATEGORIES, categoryKeys, convertUnit, formatUnitValue, unitsOf,
} from '../../lib/units';
import CopyButton from './CopyButton';
import { buttonClass, fieldClass, labelClass, mutedClass, panelClass, resultClass, tabClass } from '../uiStyles';

const UnitConverter = () => {
  const { isDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState('length');
  const [units, setUnits] = useState(UNIT_CATEGORIES.length.defaults);
  const [amount, setAmount] = useState('1');

  const [from, to] = units;
  const numeric = Number(String(amount).replace(',', '.'));
  const converted = useMemo(
    () => convertUnit(numeric, from, to, category),
    [numeric, from, to, category],
  );
  const result = formatUnitValue(converted, i18n.language);

  const pickCategory = (next) => {
    setCategory(next);
    setUnits(UNIT_CATEGORIES[next].defaults);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {categoryKeys.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => pickCategory(key)}
            className={tabClass(isDarkMode, category === key)}
          >
            {t(`useful.unit.categories.${key}`)}
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] items-end">
        <div>
          <label className={labelClass(isDarkMode)} htmlFor="unit-amount">
            {t('useful.unit.from')}
          </label>
          <div className="flex gap-2">
            <input
              id="unit-amount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode="decimal"
              className={`${fieldClass(isDarkMode)} font-mono`}
            />
            <select
              value={from}
              onChange={(event) => setUnits([event.target.value, to])}
              aria-label={t('useful.unit.from')}
              className={`${fieldClass(isDarkMode)} w-32`}
            >
              {unitsOf(category).map((unit) => <option key={unit} value={unit}>{unit}</option>)}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setUnits([to, from])}
          aria-label={t('useful.swap')}
          className={`${buttonClass(isDarkMode)} h-[42px] px-3`}
        >
          <FaExchangeAlt />
        </button>

        <div>
          <label className={labelClass(isDarkMode)} htmlFor="unit-to">
            {t('useful.unit.to')}
          </label>
          <select
            id="unit-to"
            value={to}
            onChange={(event) => setUnits([from, event.target.value])}
            className={fieldClass(isDarkMode)}
          >
            {unitsOf(category).map((unit) => <option key={unit} value={unit}>{unit}</option>)}
          </select>
        </div>
      </div>

      <div className={panelClass(isDarkMode)}>
        <p className={`text-xs mb-1 ${mutedClass(isDarkMode)}`}>{t('useful.result')}</p>
        <p className={resultClass(isDarkMode)}>
          {Number.isFinite(converted) ? `${result} ${to}` : t('useful.unit.invalid')}
        </p>
        {Number.isFinite(converted) && (
          <p className={`text-sm mt-2 ${mutedClass(isDarkMode)}`}>
            1 {from} = {formatUnitValue(convertUnit(1, from, to, category), i18n.language)} {to}
          </p>
        )}
      </div>

      <CopyButton value={Number.isFinite(converted) ? result : ''} />
    </div>
  );
};

export default UnitConverter;
